// Menu của trạm quản trị – dùng chung cho thanh bên, đường dẫn trang (breadcrumb) và ô tìm chức năng (Ctrl K).
// Khung + cách lọc theo quyền giữ của finiva-admin (tách khỏi MainLayout 15/10/2026).
import {
  Building2,
  Database,
  Factory,
  History,
  Key,
  LayoutDashboard,
  Package,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { ROUTES } from '@config/routes'
import type { CurrentUser } from '@modules/auth/types'
import { settingsPath, visibleSections } from '@modules/settings'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  requireRole?: boolean
  /** nhiều tài nguyên: hiện khi có quyền đọc ít nhất một */
  resource?: string | string[]
  /** chỉ hiện khi tạo được tài nguyên (không chỉ xem) */
  requireCreate?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Tổng quan',
    items: [{ to: ROUTES.DASHBOARD, label: 'Tổng quan', icon: LayoutDashboard }],
  },
  {
    label: 'Quản lý',
    items: [
      {
        to: ROUTES.CUSTOMERS,
        label: 'Khách hàng',
        icon: Building2,
        requireRole: true,
        resource: 'customers',
      },
      {
        to: ROUTES.COMPANIES,
        label: 'Công ty',
        icon: Factory,
        requireRole: true,
        resource: 'companies',
      },
      {
        to: ROUTES.COMPANY_ROLE_TEMPLATES,
        label: 'Vai trò mặc định & mẫu',
        icon: ShieldCheck,
        requireRole: true,
        resource: 'company-role-templates',
      },
      {
        to: ROUTES.PLANS,
        label: 'Gói',
        icon: Package,
        requireRole: true,
        resource: 'plans',
      },
      {
        to: ROUTES.ADMIN,
        label: 'Quản trị viên',
        icon: Users,
        requireRole: true,
        resource: 'admin-management',
      },
      {
        to: ROUTES.ROLES,
        label: 'Vai trò',
        icon: Key,
        requireRole: true,
        resource: 'role-management',
      },
    ],
  },
  {
    label: 'Hệ thống',
    items: [
      // Thư viện tệp (ROUTES.MEDIA, icon ImageIcon, resource media-management): tạm ẩn khỏi menu 26/10/2026 – chưa dùng; route /media vẫn giữ
      {
        to: ROUTES.DB_SERVERS,
        label: 'Máy chủ CSDL',
        icon: Database,
        requireRole: true,
        resource: 'db-servers',
      },
      {
        to: ROUTES.AUDIT_LOGS,
        label: 'Nhật ký thay đổi',
        icon: History,
        requireRole: true,
        resource: 'audit-logs',
      },
      {
        to: ROUTES.REQUEST_LOGS,
        label: 'Nhật ký truy cập',
        icon: ScrollText,
        requireRole: true,
        resource: 'request-logs',
      },
      {
        to: ROUTES.AUTH_SESSIONS,
        label: 'Phiên đăng nhập',
        icon: ShieldAlert,
        requireRole: true,
        resource: 'auth',
      },
    ],
  },
]

/** Mục menu tài khoản được thấy (thiếu quyền → ẩn) */
export function canSeeNavItem(item: NavItem, user: CurrentUser | null): boolean {
  if (!item.requireRole) return true
  if (!user?.isAdmin) return false
  if (item.requireCreate && !user.creatableResources?.includes(item.resource as string))
    return false
  if (!item.resource || !user.accessibleResources) return true
  const need = Array.isArray(item.resource) ? item.resource : [item.resource]
  return need.some((r) => user.accessibleResources!.includes(r))
}

export interface NavPage {
  to: string
  label: string
  /** nhóm menu ("Quản lý", "Cài đặt"…) – mục cha của breadcrumb, chữ phụ trong ô tìm */
  group: string
  icon: LucideIcon
}

/** Mọi trang tài khoản được vào: mục menu + các nhóm của Cài đặt (nằm cuối thanh bên) */
export function navPages(user: CurrentUser | null): NavPage[] {
  return [
    ...NAV_GROUPS.flatMap((g) =>
      g.items
        .filter((i) => canSeeNavItem(i, user))
        .map((i) => ({ to: i.to, label: i.label, group: g.label, icon: i.icon })),
    ),
    ...visibleSections(user).map((s) => ({
      to: settingsPath(s.id),
      label: s.label,
      group: 'Cài đặt',
      icon: s.icon ?? Settings,
    })),
  ]
}
